'use client';
import Image from 'next/image';
import { motion, useInView } from 'motion/react';
import { useRef } from 'react';
import { type LinkField } from 'lib/types';
import { cn } from 'lib/utils';
import { type BloomProps, Bloom, Button, RichText, Wrapper } from 'ui';

export type SpeedBumpProps = {
  className?: string;
  title: string;
  intro?: string;
  link?: LinkField;
  image: string;
  bloom?: BloomProps;
};

const SpeedBump = ({
  className,
  title,
  intro,
  link,
  image,
  bloom,
}: SpeedBumpProps) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-20%' });

  return (
    <motion.section {...{ ref }}>
      <Wrapper
        className={cn(
          'relative flex flex-col-reverse md:flex-row md:items-center gap-10 md:gap-[clamp(5rem,2.143rem+5.952vw,7.5rem)]',
          className
        )}
      >
        <Bloom {...bloom} />

        {/* Content */}
        <div className="relative z-2 flex flex-col md:justify-center grow shrink">
          {/* Header - faster transition */}
          <motion.h2
            className="text-h3"
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -40 }}
            transition={{
              duration: 0.5,
              delay: 0.05,
              ease: [0.25, 0.1, 0.25, 1],
            }}
          >
            {title}
          </motion.h2>

          {/* Content below header - slower transition */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -40 }}
            transition={{
              duration: 0.8,
              delay: 0.2,
              ease: [0.25, 0.1, 0.25, 1],
            }}
          >
            {!!intro && (
              <RichText
                className="mt-6 text-p1"
                theme="neutral"
                {...{ content: intro }}
              />
            )}

            {!!link && (
              <div className="mt-8">
                <Button
                  {...{ href: link.href, trackingProps: link.trackingProps }}
                >
                  {link.text}
                </Button>
              </div>
            )}
          </motion.div>
        </div>

        {/* Image */}
        <motion.div
          className="relative z-2 w-full aspect-[2/1] md:grow md:shrink-0 md:max-w-[clamp(20rem,-8.571rem+59.524vw,45rem)] rounded-lg overflow-hidden"
          initial={{ opacity: 0, x: 40 }}
          animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 40 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <Image
            {...{ src: image, alt: title, fill: true }}
            className="absolute w-full h-auto object-cover"
          />
        </motion.div>
      </Wrapper>
    </motion.section>
  );
};

export default SpeedBump;
